import { z } from 'zod';
import { BID_SOURCES, BID_STATUSES, BID_TYPES, BLAST_CHANNELS, BLAST_STATUSES, COMP_PRICE_KINDS, COMP_SOURCES, COMP_TYPES, CONDITION_GRADES, CONFIDENCE_LEVELS, CONSENT_STATES, DEALER_ACQ_SOURCES, DEALER_STATUSES, DEAL_OUTCOMES, DEAL_STATUSES, DISCLOSURE_STATES, EXTERNAL_COMP_SOURCES, EXTERNAL_COMP_TYPES, FALLOUT_REASONS, FEEDBACK_PARTIES, LAUNCH_BLOCKER_CODES, LAUNCH_WARNING_CODES, MESSAGE_CHANNELS, OFFER_STATUSES, PACKET_CHECK_KEYS, PASS_REASONS, PIPELINE_STAGES, PRICE_REALISM_STATUSES, RECIPIENT_SEND_STATUSES, RESPONSE_TYPES, RETAIL_EXPECTATION_RISKS, REVEAL_TYPES, SELLER_RESPONSES, SUBMISSION_SOURCES, SUBMISSION_STATUSES, TESTIMONIAL_ATTRIBUTIONS, TIER_CONTEXTS, TITLE_STATUSES, TRANSMISSIONS, VALUATION_ANCHOR_SOURCES, VALUATION_VERDICTS, } from './enums.js';
import { cents, consentSchema, dealFeedbackSchema, dealSchema, dealerContactSchema, dealerInteractionSchema, dealerIsoPrefSchema, dealerSchema, decodeSourceSchema, disclosureEventSchema, e164, isoTimestamp, mediaSummarySchema, offerSchema, optionCodeSchema, reviewTaskSchema, sellerSubmissionSchema, submissionQualificationSchema, usState, uuid, vehicleOptionSchema, vehicleSchema, vehicleSpecSchema, } from './entities.js';
/** GET /health · /healthz · /readyz */
export const healthCheckSchema = z.object({
    name: z.string(),
    ok: z.boolean(),
    latency_ms: z.number().optional(),
    detail: z.string().optional(),
});
export const healthResponseSchema = z.object({
    status: z.enum(['ok', 'degraded', 'down']),
    version: z.string(),
    uptime_s: z.number(),
    checks: z.array(healthCheckSchema),
});
/** POST /auth/login */
export const loginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1).max(1024),
    totp: z.string().length(6).optional(),
});
export const sessionUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    role: z.enum(['owner', 'operator', 'viewer']),
});
export const loginResponseSchema = z.object({
    user: sessionUserSchema,
});
/** GET /auth/me */
export const meResponseSchema = z.object({
    user: sessionUserSchema.nullable(),
});
export const errorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
// ---------------------------------------------------------------------------
// Seller intake (M1) — request/response envelopes
// ---------------------------------------------------------------------------
/** POST /public/submissions and POST /api/submissions → the public reference + workflow status. */
export const createSubmissionResponseSchema = z.object({
    id: uuid,
    public_ref: z.string(),
    status: z.enum(SUBMISSION_STATUSES),
    vin_valid: z.boolean(),
    vehicle_id: uuid.nullable(),
    consent_id: uuid.nullable(),
});
/** POST /public/submissions/:ref/media → the stored (EXIF-stripped) media descriptor. */
export const uploadMediaResponseSchema = z.object({ media: mediaSummarySchema });
/** GET /api/submissions — list filter/pagination. */
export const submissionListQuery = z.object({
    status: z.enum(SUBMISSION_STATUSES).optional(),
    source: z.enum(SUBMISSION_SOURCES).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/** A row in the operator submissions list (submission + a thin vehicle summary + media count). */
export const submissionListItemSchema = z.object({
    id: uuid,
    public_ref: z.string(),
    source: z.enum(SUBMISSION_SOURCES),
    status: z.enum(SUBMISSION_STATUSES),
    seller_name: z.string().nullable(),
    seller_region_label: z.string().nullable(),
    seller_state: z.string().nullable(),
    raw_vin: z.string().nullable(),
    vehicle_id: uuid.nullable(),
    vin_valid: z.boolean().nullable(),
    vehicle_model: z.string().nullable(),
    vehicle_model_year: z.number().int().nullable(),
    stated_mileage: z.number().int().nullable(),
    asking_price_cents: z.number().int().nullable(),
    has_consent: z.boolean(),
    media_count: z.number().int(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const submissionListResponseSchema = z.object({
    items: z.array(submissionListItemSchema),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
});
/** The consent PEWC proof artifact shown on the detail view (subset of the ledger row). */
export const consentProofSchema = consentSchema.pick({
    id: true,
    channel: true,
    consent_type: true,
    action: true,
    capture_source: true,
    consent_text_snapshot: true,
    consent_text_version: true,
    phone_e164: true,
    email: true,
    evidence_object_key: true,
    ip_address: true,
    user_agent: true,
    captured_at: true,
    created_at: true,
});
// ---------------------------------------------------------------------------
// M10 seller qualification + packet completeness + THE LAUNCH GATE — shared shapes
// (defended-against "marketplace theater": derived, testable readiness that gates blasts)
// ---------------------------------------------------------------------------
/** One derived packet-completeness checklist item (weighted; ok=satisfied). Pure/testable upstream. */
export const packetChecklistItemSchema = z.object({
    key: z.enum(PACKET_CHECK_KEYS),
    label: z.string(),
    weight: z.number().int(),
    ok: z.boolean(),
    detail: z.string(),
});
/** The derived 0–100 packet-completeness score + its per-item checklist + what is still missing. */
export const packetCompletenessSchema = z.object({
    score: z.number().int(), // 0..100 (sum of satisfied item weights)
    max: z.number().int(), // always 100 (an explicit, stable denominator)
    complete: z.boolean(), // score >= the launch threshold
    threshold: z.number().int(), // the launch threshold the score is measured against
    checklist: z.array(packetChecklistItemSchema),
    missing: z.array(z.enum(PACKET_CHECK_KEYS)),
});
/**
 * An estimated plausible dealer-bid band. The M11 valuation oracle injects this (source 'valuation');
 * until then the launch gate falls back to the internal comps oracle (source 'comps_oracle') for the
 * same spec fingerprint, else reports no estimate (a soft warning, never a hard block — the M11 seam).
 */
export const estimatedBandSchema = z.object({
    low_cents: cents,
    high_cents: cents,
    source: z.string(), // 'valuation' (M11) | 'comps_oracle' | 'operator'
    sample_size: z.number().int().nullable(),
});
/** The price-realism verdict: the seller's anchor (floor ?? asking) vs. the estimated dealer-bid band. */
export const priceRealismSchema = z.object({
    status: z.enum(PRICE_REALISM_STATUSES),
    anchor_cents: cents.nullable(), // seller_floor_cents ?? asking_price_cents
    band: estimatedBandSchema.nullable(),
    detail: z.string(),
});
/**
 * The launch gate: the four hard checks + network coverage + price realism + soft warnings.
 * Refined model (2026-07-07): coverage is the FRANCHISE NETWORK — "≥ K active franchise dealers
 * reachable (consented + unsuppressed)" — not ISO matching. ISO matching remains an OPTIONAL
 * priority/highlight signal, surfaced via matched_dealer_count / named_buyer_count (informational).
 */
export const launchReadinessSchema = z.object({
    ready: z.boolean(), // ALL hard checks pass
    blockers: z.array(z.enum(LAUNCH_BLOCKER_CODES)),
    warnings: z.array(z.enum(LAUNCH_WARNING_CODES)),
    reasons: z.array(z.string()), // human-readable lines (one per blocker/warning)
    has_consent: z.boolean(),
    packet_score: z.number().int(),
    packet_threshold: z.number().int(),
    active_franchise_dealer_count: z.number().int(), // the broadcast network (status=active AND franchise)
    matched_dealer_count: z.number().int(), // dealers with a live matching ISO (OPTIONAL priority signal, not a gate)
    reachable_dealer_count: z.number().int(), // active franchise dealers consented + unsuppressed — THE coverage metric
    named_buyer_count: z.number().int(), // of ISO-matched, buyer-in-hand (highlight signal)
    coverage_threshold: z.number().int(), // K
    price_realism: priceRealismSchema,
    retail_expectation_risk: z.enum(RETAIL_EXPECTATION_RISKS).nullable(),
    accepts_dealer_direct: z.boolean().nullable(),
});
/** PUT /api/submissions/:id/qualification → the upserted qualification row. */
export const qualifyResponseSchema = z.object({ qualification: submissionQualificationSchema });
/**
 * M11 — a COMPACT persisted-valuation summary surfaced on the submission detail + the liquidity tower
 * (defined here, before submissionDetailSchema, which references it). The full worksheet lives at
 * GET /api/submissions/:id/valuation (valuationSchema, further below).
 */
export const valuationSummarySchema = z.object({
    verdict: z.enum(VALUATION_VERDICTS),
    confidence: z.enum(CONFIDENCE_LEVELS),
    dealer_acq_low_cents: cents.nullable(),
    dealer_acq_high_cents: cents.nullable(),
    seller_anchor_cents: cents.nullable(),
    gap_cents: cents.nullable(),
    /** true when this valuation is wired into the launch gate (source 'valuation'). */
    use_for_gate: z.boolean(),
    updated_at: isoTimestamp,
});
/** GET /api/submissions/:id — full detail: submission + resolved vehicle + media + consent proof. */
export const submissionDetailSchema = z.object({
    submission: sellerSubmissionSchema,
    vehicle: vehicleSchema.nullable(),
    media: z.array(mediaSummarySchema),
    consent: consentProofSchema.nullable(),
    /** M2: the resolved spec + options, when the vehicle has been decoded. */
    spec: vehicleSpecSchema.nullable(),
    options: z.array(vehicleOptionSchema),
    /** M10: seller qualification + derived packet score + launch-gate readiness (surfaced prominently). */
    qualification: submissionQualificationSchema.nullable(),
    packet: packetCompletenessSchema,
    launch_readiness: launchReadinessSchema,
    /** M11: the persisted appraisal verdict/band, when the operator has saved one (null otherwise). */
    valuation: valuationSummarySchema.nullable(),
});
// ---------------------------------------------------------------------------
// Decode / spec review (M2) — request/response envelopes
// ---------------------------------------------------------------------------
/** A decoded option with its dictionary mapping resolved (for the spec-review option list). */
export const resolvedOptionSchema = vehicleOptionSchema.extend({
    /** the dictionary category, when the code is mapped. */
    category: z.string().nullable(),
    /** dictionary confidence for the mapping, when mapped. */
    dictionary_confidence: z.string().nullable(),
});
/** GET /api/vehicles/:id/spec — the operator spec-review packet (spec + options + provenance + tasks). */
export const specReviewSchema = z.object({
    vehicle: vehicleSchema,
    spec: vehicleSpecSchema.nullable(),
    options: z.array(resolvedOptionSchema),
    sources: z.array(decodeSourceSchema),
    dictionary_entries: z.array(optionCodeSchema),
    review_tasks: z.array(reviewTaskSchema),
});
/** POST /api/submissions/:id/decode and /api/vehicles/:id/decode/paid → the decode outcome. */
export const decodeResultSchema = z.object({
    vehicle: vehicleSchema,
    spec: vehicleSpecSchema.nullable(),
    options_count: z.number().int(),
    decode_status: z.string(),
    paid_api_status: z.enum(['ok', 'not_configured', 'budget_exceeded', 'failed', 'skipped']).optional(),
    cost_cents: z.number().int().optional(),
});
/** GET /api/dictionary/resolve → a single dictionary hit (or null). */
export const dictionaryResolveResponseSchema = z.object({
    match: optionCodeSchema.nullable(),
});
// ---------------------------------------------------------------------------
// M3 dealer network — response envelopes
// ---------------------------------------------------------------------------
/** A dealer contact plus its DERIVED effective consent state per channel (from the ledger). */
export const dealerContactWithConsentSchema = dealerContactSchema.extend({
    /** effective SMS consent (granted once a business-consent row exists + not suppressed). */
    sms_consent: z.enum(CONSENT_STATES),
    /** effective email consent. */
    email_consent: z.enum(CONSENT_STATES),
    /** true when this contact can be reached on at least one channel (the compliance gate would pass consent). */
    reachable: z.boolean(),
});
/** A row in the operator dealer directory list. */
export const dealerListItemSchema = z.object({
    id: uuid,
    business_name: z.string(),
    state: z.string(),
    status: z.enum(DEALER_STATUSES),
    /** Refined model — official Porsche franchise store (only franchise dealers are blasted). */
    franchise: z.boolean(),
    subscription_tier: z.string().nullable(),
    contact_count: z.number().int(),
    iso_pref_count: z.number().int(),
    active_iso_pref_count: z.number().int(),
    has_named_buyer: z.boolean(),
    reachable_contact_count: z.number().int(),
    // M9 recruitment funnel surfaced on the list.
    pipeline_stage: z.enum(PIPELINE_STAGES),
    relationship_owner_id: uuid.nullable(),
    relationship_owner_name: z.string().nullable(),
    last_interaction_at: isoTimestamp.nullable(),
    next_follow_up_at: isoTimestamp.nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const dealerListResponseSchema = z.object({
    items: z.array(dealerListItemSchema),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
});
/**
 * Per-dealer response-rate history, DERIVED from blast_recipients + bids (§3.5). "Which dealers
 * actually show up." `reached` = recipients we successfully sent/delivered to (the fair denominator);
 * rates are null when reached=0 (never divide by zero / never assert a rate we can't compute).
 */
export const dealerResponseStatsSchema = z.object({
    asked: z.number().int(),
    reached: z.number().int(),
    responded: z.number().int(),
    bids: z.number().int(),
    no_bids: z.number().int(),
    response_rate: z.number().nullable(),
    bid_rate: z.number().nullable(),
    avg_turnaround_hours: z.number().nullable(),
    last_responded_at: isoTimestamp.nullable(),
});
/** GET /api/dealers/:id — full detail: dealer + contacts + ISO prefs + interactions + response history. */
export const dealerDetailSchema = z.object({
    dealer: dealerSchema,
    contacts: z.array(dealerContactWithConsentSchema),
    iso_prefs: z.array(dealerIsoPrefSchema),
    interactions: z.array(dealerInteractionSchema),
    response_stats: dealerResponseStatsSchema,
});
/** POST /api/dealers/:id/interactions → the logged touch + the dealer's resulting stage. */
export const logInteractionResponseSchema = z.object({
    interaction: dealerInteractionSchema,
    pipeline_stage: z.enum(PIPELINE_STAGES),
});
/**
 * POST /api/dealers/:id/live-iso → the created ISO pref (LIVE for matching immediately) + the dealer's
 * new stage + the count of active prefs now on the dealer (proof the capture is matchable).
 */
export const captureLiveIsoResponseSchema = z.object({
    iso_pref: dealerIsoPrefSchema,
    dealer_id: uuid,
    pipeline_stage: z.enum(PIPELINE_STAGES),
    interaction_id: uuid.nullable(),
    active_iso_pref_count: z.number().int(),
});
/** One row of a CSV import result (created | updated | skipped | error) with a human detail string. */
export const dealerImportRowResultSchema = z.object({
    index: z.number().int(),
    dealership: z.string(),
    /** M15/M5 — 'needs_review' = a same-name-same-state identity collision the import REFUSED to merge. */
    outcome: z.enum(['created', 'updated', 'skipped', 'needs_review', 'error']),
    dealer_id: uuid.nullable(),
    contact_id: uuid.nullable(),
    detail: z.string(),
});
/** POST /api/dealers/import → the dry-run/live import report (counts + per-row outcomes). */
export const dealerImportResultSchema = z.object({
    dry_run: z.boolean(),
    total: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
    /** M15/M5 — rows held back as identity collisions (nothing written; resolve manually). */
    needs_review: z.number().int(),
    errors: z.number().int(),
    rows: z.array(dealerImportRowResultSchema),
});
// ---------------------------------------------------------------------------
// M9 recruitment cockpit dashboards — GET /api/cockpit/active-isos + /api/cockpit/pipeline
// ---------------------------------------------------------------------------
/** One live ISO on the active-ISO dashboard: the demand + the dealer + its response history. */
export const activeIsoEntrySchema = z.object({
    iso_pref_id: uuid,
    dealer_id: uuid,
    dealer_business_name: z.string(),
    dealer_state: z.string(),
    dealer_status: z.enum(DEALER_STATUSES),
    pipeline_stage: z.enum(PIPELINE_STAGES),
    label: z.string(),
    models: z.array(z.string()),
    generations: z.array(z.string()),
    transmission: z.array(z.string()),
    year_min: z.number().int().nullable(),
    year_max: z.number().int().nullable(),
    mileage_max: z.number().int().nullable(),
    budget_max_cents: cents.nullable(),
    required_option_codes: z.array(z.string()),
    has_named_buyer: z.boolean(),
    /** the dealer has at least one reachable (consented, unsuppressed) contact. */
    reachable: z.boolean(),
    response_stats: dealerResponseStatsSchema,
    updated_at: isoTimestamp,
});
/** Live ISOs grouped by model/spec (the demand clusters a founder sources against). */
export const activeIsoGroupSchema = z.object({
    key: z.string(),
    count: z.number().int(),
    named_buyer_count: z.number().int(),
    entries: z.array(activeIsoEntrySchema),
});
/** GET /api/cockpit/active-isos — all live ISOs grouped by demand cluster. */
export const activeIsoDashboardSchema = z.object({
    total_active_isos: z.number().int(),
    named_buyer_isos: z.number().int(),
    groups: z.array(activeIsoGroupSchema),
});
/** A dealer chip inside a pipeline-stage column. */
export const pipelineDealerSchema = z.object({
    id: uuid,
    business_name: z.string(),
    state: z.string(),
    status: z.enum(DEALER_STATUSES),
    relationship_owner_name: z.string().nullable(),
    active_iso_pref_count: z.number().int(),
    has_named_buyer: z.boolean(),
    last_interaction_at: isoTimestamp.nullable(),
});
/** One column of the recruitment pipeline board. */
export const pipelineStageBucketSchema = z.object({
    stage: z.enum(PIPELINE_STAGES),
    count: z.number().int(),
    dealers: z.array(pipelineDealerSchema),
});
/** A due (or overdue) follow-up reminder derived from dealer_interactions. */
export const followUpReminderSchema = z.object({
    interaction_id: uuid,
    dealer_id: uuid,
    dealer_business_name: z.string(),
    pipeline_stage: z.enum(PIPELINE_STAGES),
    follow_up_at: isoTimestamp,
    follow_up_note: z.string().nullable(),
    overdue: z.boolean(),
});
/** GET /api/cockpit/pipeline — the recruitment board (dealers by stage) + due follow-up reminders. */
export const pipelineBoardSchema = z.object({
    stages: z.array(pipelineStageBucketSchema),
    due_reminders: z.array(followUpReminderSchema),
});
// ---------------------------------------------------------------------------
// M10 LIQUIDITY CONTROL TOWER — GET /api/cockpit/liquidity
// One row per active submission: packet + dealer coverage + the launch gate + live bid tallies +
// top pass-reason taxonomy + close outcome. The one screen where a founder sees exactly which cars
// are ready to send, blocked, or should be rejected (the direct defense against "marketplace theater").
// ---------------------------------------------------------------------------
/** A pass-reason count in a submission's demand-signal taxonomy (why dealers declined). */
export const passReasonTallySchema = z.object({ reason: z.enum(PASS_REASONS), count: z.number().int() });
/** One control-tower row: everything a founder needs to triage a submission at a glance. */
export const liquidityTowerRowSchema = z.object({
    submission_id: uuid,
    public_ref: z.string(),
    status: z.enum(SUBMISSION_STATUSES),
    vehicle_id: uuid.nullable(),
    model_designation: z.string().nullable(),
    model_year: z.number().int().nullable(),
    region_label: z.string().nullable(),
    // readiness (the launch gate):
    packet_score: z.number().int(),
    packet_missing: z.array(z.enum(PACKET_CHECK_KEYS)),
    launch_ready: z.boolean(),
    launch_blockers: z.array(z.enum(LAUNCH_BLOCKER_CODES)),
    launch_warnings: z.array(z.enum(LAUNCH_WARNING_CODES)),
    // dealer coverage:
    matched_dealer_count: z.number().int(), // # with a live matching ISO
    reachable_dealer_count: z.number().int(), // # relevant opted-in (consented) dealers
    named_buyer_count: z.number().int(), // buyer-in-hand count
    // seller floor vs. estimated dealer-bid band:
    seller_floor_cents: cents.nullable(),
    asking_price_cents: cents.nullable(),
    estimated_band: estimatedBandSchema.nullable(),
    price_realism: z.enum(PRICE_REALISM_STATUSES),
    /** M11: the persisted appraisal verdict, when the operator has saved one for this car (null otherwise). */
    valuation_verdict: z.enum(VALUATION_VERDICTS).nullable(),
    // seller anchor risk (so a founder never launches a mis-anchored seller):
    retail_expectation_risk: z.enum(RETAIL_EXPECTATION_RISKS).nullable(),
    accepts_dealer_direct: z.boolean().nullable(),
    // live bid picture across this submission's blasts:
    latest_blast_id: uuid.nullable(),
    bid_deadline: isoTimestamp.nullable(),
    asked_count: z.number().int(),
    sent_count: z.number().int(),
    bid_count: z.number().int(),
    no_bid_count: z.number().int(),
    ignored_count: z.number().int(),
    top_pass_reasons: z.array(passReasonTallySchema),
    // close outcome:
    deal_id: uuid.nullable(),
    deal_status: z.enum(DEAL_STATUSES).nullable(),
    close_outcome: z.string().nullable(),
    updated_at: isoTimestamp,
});
/** GET /api/cockpit/liquidity — list filter. `include_closed` also surfaces closed_won/closed_lost cars. */
export const liquidityTowerQuery = z.object({
    include_closed: z
        .enum(['true', 'false'])
        .default('false')
        .transform((v) => v === 'true'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** GET /api/cockpit/liquidity — the control tower: per-submission readiness rows + a fleet summary. */
export const liquidityTowerSchema = z.object({
    generated_at: isoTimestamp,
    summary: z.object({
        total: z.number().int(),
        ready: z.number().int(),
        blocked: z.number().int(),
        launched: z.number().int(), // has at least one blast
        with_bids: z.number().int(),
        closed_won: z.number().int(),
    }),
    rows: z.array(liquidityTowerRowSchema),
});
/** POST /api/dealers → the created dealer id + status. */
export const createDealerResponseSchema = z.object({ id: uuid, status: z.enum(DEALER_STATUSES) });
/** POST /api/dealers/onboard → the created dealer + its contacts + how many consents were captured. */
export const onboardDealerResponseSchema = z.object({
    dealer_id: uuid,
    contact_ids: z.array(uuid),
    consents_captured: z.number().int(),
});
// ---------------------------------------------------------------------------
// M8 public dealer-interest funnel — POST /dealer-interest (no login, no SMS consent)
// ---------------------------------------------------------------------------
/**
 * Public dealer cold-start lead — FRANCHISE-STORE signups (refined model: the network is exclusively
 * official Porsche franchise stores at launch, so the public form is a franchise-store funnel and the
 * created prospect is marked `franchise=true`). Creates a `dealers` + `dealer_contacts` PROSPECT via
 * the existing dealer service — deliberately WITHOUT capturing any SMS/B2B consent (that happens
 * later in operator-driven onboarding). A contact needs at least a work email or phone.
 */
export const dealerInterestInput = z
    .object({
    name: z.string().min(1).max(200),
    dealership: z.string().min(1).max(200),
    state: usState,
    /** store info: the city of the franchise store (identity disambiguator). */
    city: z.string().min(1).max(120).optional(),
    email: z.string().email().optional(),
    phone_e164: e164.optional(),
    specs: z.string().max(2000).optional(),
})
    .refine((c) => Boolean(c.email || c.phone_e164), {
    message: 'provide a work email or phone so we can reach you',
    path: ['email'],
});
/** POST /dealer-interest → the created prospect dealer + its lead contact. */
export const dealerInterestResponseSchema = z.object({
    dealer_id: uuid,
    contact_id: uuid,
    status: z.enum(DEALER_STATUSES),
});
// ---------------------------------------------------------------------------
// M3 matching engine — GET /api/submissions/:id/matches
// ---------------------------------------------------------------------------
/** One ranked match: the dealer + its reachable contact + the ISO pref that matched + score + reasons. */
export const matchPreviewItemSchema = z.object({
    dealer_id: uuid,
    dealer_business_name: z.string(),
    dealer_state: z.string(),
    dealer_status: z.enum(DEALER_STATUSES),
    dealer_contact_id: uuid.nullable(),
    contact_name: z.string().nullable(),
    contact_phone_e164: z.string().nullable(),
    contact_email: z.string().nullable(),
    /** the primary contact's derived reachability (a preview hint; the gate re-checks at send time in M4/M5). */
    contact_reachable: z.boolean(),
    iso_pref_id: uuid,
    iso_pref_label: z.string(),
    /** THE WEDGE, surfaced first-class so the UI can highlight buyer-in-hand dealers. */
    has_named_buyer: z.boolean(),
    score: z.number(),
    /** structured JSON: which HARD facets matched + the SOFT boost breakdown + has_named_buyer. */
    reasons: z.record(z.unknown()),
});
/** GET /api/submissions/:id/matches — the ranked dealer candidates for the submission's resolved spec. */
export const matchesResponseSchema = z.object({
    submission_id: uuid,
    vehicle_id: uuid.nullable(),
    /** false when the vehicle is not yet decoded/specced → matches is empty (nothing to match against). */
    spec_ready: z.boolean(),
    spec: vehicleSpecSchema.nullable(),
    match_count: z.number().int(),
    matches: z.array(matchPreviewItemSchema),
});
// ---------------------------------------------------------------------------
// M5 blasts + bid capture (the data moat turns on) — architecture.md §3.5–§3.7, §5.4
// ---------------------------------------------------------------------------
/** A single MASKED option shown to dealers (code + our own description; NEVER seller PII). */
export const maskedOptionSchema = z.object({ code: z.string(), description: z.string().nullable() });
/**
 * THE MASKED PAYLOAD (§5.4, refined model 2026-07-07). Masking protects the SELLER, not the car:
 * the packet carries the decoded spec + coarse region + watermarked photos + the FULL VIN (founder
 * decision #2 — dealers run Carfax/history and bid with confidence; a VIN does not yield the owner's
 * contact, which is DPPA-restricted), and STRUCTURALLY nothing that identifies the seller: no
 * name/phone/email. The `BlastService` builds this from a masked view that cannot reach
 * `seller_submissions` PII columns; tests assert the payload carries the full VIN while PII is absent.
 */
export const maskedBlastPacketSchema = z.object({
    model_designation: z.string().nullable(),
    generation: z.string().nullable(),
    model_year: z.number().int().nullable(),
    transmission: z.string(),
    paint_to_sample: z.boolean(),
    exterior_color: z.string().nullable(),
    pts_color_name: z.string().nullable(),
    interior: z.string().nullable(),
    mileage: z.number().int().nullable(),
    region_label: z.string().nullable(),
    /** the FULL 17-char VIN (transparency on the car; seller PII stays masked) — null when undecoded. */
    vin: z.string().nullable(),
    total_msrp_cents: cents.nullable(),
    spec_confidence: z.enum(CONFIDENCE_LEVELS).nullable(),
    highlight_options: z.array(maskedOptionSchema),
    /** ONLY watermarked derivative keys — never the GPS-bearing original object_key. */
    watermarked_photo_keys: z.array(z.string()),
});
/** A compact bid summary hung off a recipient (the moat's numerator). */
export const bidSummarySchema = z.object({
    id: uuid,
    bid_origin: z.string(),
    bid_type: z.enum(BID_TYPES),
    amount_cents: cents.nullable(),
    trade_description: z.string().nullable(),
    trade_allowance_cents: cents.nullable(),
    pass_reason: z.enum(PASS_REASONS).nullable(),
    pass_reason_text: z.string().nullable(),
    conditions: z.string().nullable(),
    source: z.enum(BID_SOURCES),
    status: z.enum(BID_STATUSES),
    created_at: isoTimestamp,
});
/** ONE recipient row = one dealer we asked (the denominator) + its send status + response. */
export const blastRecipientItemSchema = z.object({
    id: uuid,
    dealer_id: uuid,
    dealer_business_name: z.string(),
    dealer_state: z.string(),
    dealer_contact_id: uuid.nullable(),
    contact_name: z.string().nullable(),
    contact_phone_e164: z.string().nullable(),
    contact_email: z.string().nullable(),
    iso_pref_id: uuid.nullable(),
    iso_pref_label: z.string().nullable(),
    has_named_buyer: z.boolean(),
    match_score: z.number().nullable(),
    channel: z.enum(MESSAGE_CHANNELS),
    send_status: z.enum(RECIPIENT_SEND_STATUSES),
    skip_reason: z.string().nullable(),
    responded: z.boolean(),
    response_type: z.enum(RESPONSE_TYPES),
    bid: bidSummarySchema.nullable(),
    responded_at: isoTimestamp.nullable(),
    created_at: isoTimestamp,
});
/** The blast campaign header. */
export const blastSummarySchema = z.object({
    id: uuid,
    seller_submission_id: uuid,
    vehicle_id: uuid,
    status: z.enum(BLAST_STATUSES),
    channel: z.enum(BLAST_CHANNELS),
    disclosure_level: z.string(),
    recipient_count: z.number().int(),
    sent_at: isoTimestamp.nullable(),
    closes_at: isoTimestamp.nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
/** Recipient send-status + response tallies — the moat picture (asked → sent → bid/no-bid/ignored). */
export const blastBreakdownSchema = z.object({
    total: z.number().int(),
    pending: z.number().int(),
    /** M14/M4 — recipients currently held by fan-out's atomic claim (transient; resolves to a terminal status). */
    sending: z.number().int(),
    sent: z.number().int(),
    delivered: z.number().int(),
    consent_blocked: z.number().int(),
    suppressed: z.number().int(),
    nj_blocked: z.number().int(),
    window_deferred: z.number().int(),
    scheduled: z.number().int(),
    failed: z.number().int(),
    skipped: z.number().int(),
    ignored: z.number().int(),
    bid: z.number().int(),
    no_bid: z.number().int(),
});
/** A parsed-but-UNCONFIRMED inbound-SMS bid awaiting operator confirmation (a review_tasks row). */
export const suggestedBidSchema = z.object({
    review_task_id: uuid,
    blast_recipient_id: uuid,
    dealer_id: uuid,
    suggestion: z.object({
        bid_type: z.enum(BID_TYPES).nullable(),
        amount_cents: cents.nullable(),
        pass_reason: z.enum(PASS_REASONS).nullable(),
        confidence: z.string(),
        ambiguous: z.boolean(),
    }),
    raw_body: z.string(),
    created_at: isoTimestamp,
});
/** GET/POST blast detail — the composer preview + the detail view share this shape. */
export const blastDetailSchema = z.object({
    blast: blastSummarySchema,
    masked_packet: maskedBlastPacketSchema,
    recipients: z.array(blastRecipientItemSchema),
    breakdown: blastBreakdownSchema,
    suggested_bids: z.array(suggestedBidSchema),
    /** M10 — the CURRENT launch-gate readiness for this blast's submission + whether it was launched via override. */
    launch_readiness: launchReadinessSchema.nullable(),
    overridden: z.boolean(),
});
export const blastListItemSchema = blastSummarySchema.extend({
    responded_count: z.number().int(),
    bid_count: z.number().int(),
});
export const blastListResponseSchema = z.object({ items: z.array(blastListItemSchema) });
/**
 * POST /api/submissions/:id/blasts — create + preview. Refined model (2026-07-07): a blast is a
 * BROADCAST to every active franchise dealer with a contact — there is no ISO-pref curation of the
 * recipient set (ISO matching only prioritizes/highlights recipients). The former `iso_pref_ids`
 * filter is intentionally gone: a dealer with no ISO still receives the car.
 */
export const createBlastInput = z.object({
    channel: z.enum(BLAST_CHANNELS).optional(),
    template_key: z.string().min(1).max(80).optional(),
    closes_at: isoTimestamp.optional(),
    /**
     * M10 — an EXPLICIT, audited operator override of a FAILED launch gate (a judgment call). Requires a
     * reason; the reason + the blockers it overrode are recorded on the blast + written to the audit log.
     */
    launch_override_reason: z.string().min(1).max(1000).optional(),
});
/**
 * POST /api/blasts/:id/send — optional body. M14/H1: the launch gate re-runs AT SEND TIME (a STOP
 * between create and send hard-stops the fan-out); a still-blocked send may only proceed with a
 * FRESH, audited override reason (the create-time override does NOT carry over).
 */
export const sendBlastInput = z.object({
    launch_override_reason: z.string().min(1).max(1000).optional(),
});
/** POST /api/blasts/:id/send — the fan-out outcome (per-recipient send_status set by the compliance gate). */
export const sendBlastResponseSchema = z.object({
    blast_id: uuid,
    total: z.number().int(),
    sent: z.number().int(),
    deferred: z.number().int(),
    blocked: z.number().int(),
    skipped: z.number().int(),
    failed: z.number().int(),
    breakdown: blastBreakdownSchema,
    recipients: z.array(blastRecipientItemSchema),
});
/**
 * POST /api/blast-recipients/:id/bid — operator enters/confirms a dealer bid. `bid_type` in
 * {cash,trade,cash_plus_trade,no_bid,pass}; priced bids require amount_cents (mirrors the DB CHECK).
 * dealer/vehicle/blast linkage is derived from the recipient (the moat join).
 */
export const enterBidInput = z
    .object({
    bid_type: z.enum(BID_TYPES),
    amount_cents: cents.positive().optional(),
    trade_description: z.string().max(2000).optional(),
    trade_allowance_cents: cents.nonnegative().optional(),
    pass_reason: z.enum(PASS_REASONS).optional(),
    pass_reason_text: z.string().max(2000).optional(),
    conditions: z.string().max(2000).optional(),
    source: z.enum(BID_SOURCES).default('sms_operator_entered'),
    is_binding: z.boolean().optional(),
    raw_sms_message_id: uuid.optional(),
    /** when confirming a parsed suggestion, the review task to resolve. */
    review_task_id: uuid.optional(),
})
    .refine((b) => b.bid_type === 'no_bid' || b.bid_type === 'pass' || b.amount_cents != null, {
    message: 'priced bids (cash/trade/cash_plus_trade) require amount_cents',
    path: ['amount_cents'],
});
/** POST /api/blast-recipients/:id/suggest-bid — parse an inbound SMS into a SUGGESTED bid (no auto-accept). */
export const suggestBidInput = z.object({
    from_e164: e164.optional(),
    body: z.string().min(1).max(1600),
    raw_sms_message_id: uuid.optional(),
});
/** PATCH /api/suggested-bids/:id — confirm a suggested bid (operator may edit before recording). */
export const confirmSuggestedBidInput = z
    .object({
    bid_type: z.enum(BID_TYPES).optional(),
    amount_cents: cents.positive().nullable().optional(),
    trade_description: z.string().max(2000).optional(),
    trade_allowance_cents: cents.nonnegative().optional(),
    pass_reason: z.enum(PASS_REASONS).optional(),
    pass_reason_text: z.string().max(2000).optional(),
    conditions: z.string().max(2000).optional(),
    /**
     * M15/M7 — REQUIRED when the suggestion's attribution is ambiguous (>1 recent active blast leg
     * for the sender): the operator picks WHICH candidate leg (car) the dealer actually answered.
     */
    blast_recipient_id: uuid.optional(),
})
    .default({});
/** PATCH /api/bids/:id — edit/withdraw a recorded bid. */
export const updateBidInput = z
    .object({
    status: z.enum(BID_STATUSES).optional(),
    amount_cents: cents.positive().nullable().optional(),
    conditions: z.string().max(2000).nullable().optional(),
})
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one field is required' });
/** The result of recording/confirming a bid: the bid + the comp it wrote + the recipient transition. */
export const bidResponseSchema = z.object({
    bid: bidSummarySchema,
    comp_id: uuid.nullable(),
    response_type: z.enum(RESPONSE_TYPES).nullable(),
});
// ---------------------------------------------------------------------------
// M6 offers + staged disclosure + deals + comps-on-close — architecture.md §3.5/§3.6, §4.2, §5.4, §7
// ---------------------------------------------------------------------------
// --- operator: curate the offer menu ---------------------------------------
/** POST /api/submissions/:id/offers — add/update a curated offer from a priced bid (rank optional). */
export const createOfferInput = z.object({
    bid_id: uuid,
    rank: z.number().int().min(1).max(50).optional(),
});
/** PUT /api/submissions/:id/offers — set the whole menu at once (bids + ranks). */
export const curateOffersInput = z.object({
    items: z
        .array(z.object({ bid_id: uuid, rank: z.number().int().min(1).max(50).optional() }))
        .min(1)
        .max(50),
});
/** PATCH /api/offers/:id — re-rank or withdraw a draft offer. */
export const updateOfferInput = z
    .object({
    rank: z.number().int().min(1).max(50).nullable().optional(),
    status: z.literal('withdrawn').optional(),
})
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one field is required' });
/** POST /api/submissions/:id/offers/present — mint the seller magic-link + send the menu. */
export const presentOffersInput = z.object({ expires_at: isoTimestamp.optional() }).default({});
/** POST /api/offers/:id/reveal — operator-manual disclosure (audited, trigger_reason='operator_manual'). */
export const revealOfferInput = z.object({ reveal_type: z.enum(REVEAL_TYPES).optional() }).default({});
// --- public seller offer-menu (magic-link, no login) -----------------------
/**
 * A single offer as the SELLER sees it. Dealer IDENTITY is MASKED (coarse region + quality signal only)
 * until a `dealer_identity_to_seller` disclosure exists — then `dealer_business_name` is populated.
 */
export const sellerOfferItemSchema = z.object({
    offer_id: uuid,
    rank: z.number().int().nullable(),
    amount_cents: cents,
    status: z.enum(OFFER_STATUSES),
    disclosure_state: z.enum(DISCLOSURE_STATES),
    seller_response: z.enum(SELLER_RESPONSES).nullable(),
    /** coarse US region derived from the dealer's state (never the exact dealer). */
    dealer_region: z.string(),
    dealer_status: z.enum(DEALER_STATUSES),
    /** THE WEDGE surfaced as a quality signal without identifying the dealer. */
    has_named_buyer: z.boolean(),
    /** null until staged disclosure reveals the dealer to the seller (on accept). */
    dealer_business_name: z.string().nullable(),
});
/** GET /public/offers/:token — the seller's masked offer menu. */
export const sellerOfferMenuSchema = z.object({
    submission_ref: z.string(),
    vehicle: z.object({
        model_designation: z.string().nullable(),
        model_year: z.number().int().nullable(),
        mileage: z.number().int().nullable(),
    }),
    offers: z.array(sellerOfferItemSchema),
    expires_at: isoTimestamp.nullable(),
    responded: z.boolean(),
    accepted_offer_id: uuid.nullable(),
});
/** POST /public/offers/:token/respond — seller accepts / declines / counters one offer. */
export const offerRespondInput = z
    .object({
    offer_id: uuid,
    response: z.enum(['accepted', 'declined', 'counter']),
    counter_amount_cents: cents.positive().optional(),
})
    .refine((o) => o.response !== 'counter' || o.counter_amount_cents != null, {
    message: 'a counter requires counter_amount_cents',
    path: ['counter_amount_cents'],
});
/** POST /public/offers/:token/respond → the outcome + (on accept) the created deal + the revealed dealer. */
export const offerRespondResponseSchema = z.object({
    offer_id: uuid,
    status: z.enum(OFFER_STATUSES),
    seller_response: z.enum(SELLER_RESPONSES),
    deal_id: uuid.nullable(),
    dealer_revealed: z.boolean(),
    dealer_business_name: z.string().nullable(),
});
// --- operator: offer-menu builder view -------------------------------------
/** A priced bid available to curate into an offer (the builder's candidate list). */
export const offerCandidateBidSchema = z.object({
    bid_id: uuid,
    blast_id: uuid.nullable(),
    blast_recipient_id: uuid.nullable(),
    dealer_id: uuid,
    dealer_business_name: z.string(),
    dealer_state: z.string(),
    has_named_buyer: z.boolean(),
    bid_type: z.enum(BID_TYPES),
    amount_cents: cents.nullable(),
    created_at: isoTimestamp,
    is_offered: z.boolean(),
    offer_id: uuid.nullable(),
});
/** A curated offer row in the operator builder (offer + resolved dealer/bid context). */
export const offerRowSchema = offerSchema.extend({
    dealer_id: uuid,
    dealer_business_name: z.string(),
    dealer_state: z.string(),
    has_named_buyer: z.boolean(),
    bid_amount_cents: cents.nullable(),
    bid_type: z.enum(BID_TYPES),
});
/** GET /api/submissions/:id/offers — the offer-menu builder (offers + candidates + seller preview). */
export const offerBuilderSchema = z.object({
    submission_id: uuid,
    submission_status: z.enum(SUBMISSION_STATUSES),
    offers: z.array(offerRowSchema),
    candidate_bids: z.array(offerCandidateBidSchema),
    /** EXACTLY what the seller will see (masked) — the operator previews before presenting. */
    seller_preview: sellerOfferMenuSchema,
    presented: z.boolean(),
    /** the seller magic-link URL, populated after present (so the operator can re-share it). */
    seller_menu_url: z.string().nullable(),
});
/** POST /api/submissions/:id/offers/present → the minted seller link + the send outcome. */
export const presentOffersResponseSchema = z.object({
    submission_id: uuid,
    offer_count: z.number().int(),
    menu_url: z.string(),
    token: z.string(),
    send_status: z.string(),
    channel: z.string().nullable(),
});
// --- public dealer bid form (magic-link, no login) — the FORMAL bid act -----
/** GET /public/bid/:token — the masked vehicle packet a dealer bids against (+ any prior bid). */
export const publicBidViewSchema = z.object({
    recipient_id: uuid,
    packet: maskedBlastPacketSchema,
    closed: z.boolean(),
    already_bid: z.boolean(),
    bid: bidSummarySchema.nullable(),
});
/** POST /public/bid/:token — a dealer places a structured bid (or pass). A PRICED bid is the formal act. */
export const publicBidInput = z
    .object({
    bid_type: z.enum(BID_TYPES),
    amount_cents: cents.positive().optional(),
    trade_description: z.string().max(2000).optional(),
    trade_allowance_cents: cents.nonnegative().optional(),
    pass_reason: z.enum(PASS_REASONS).optional(),
    pass_reason_text: z.string().max(2000).optional(),
    conditions: z.string().max(2000).optional(),
})
    .refine((b) => b.bid_type === 'no_bid' || b.bid_type === 'pass' || b.amount_cents != null, {
    message: 'priced bids (cash/trade/cash_plus_trade) require amount_cents',
    path: ['amount_cents'],
});
/** POST /public/bid/:token → the recorded bid + (for a priced/formal bid) the disclosure it wrote. */
export const publicBidResponseSchema = z.object({
    bid: bidSummarySchema,
    comp_id: uuid.nullable(),
    /** the seller-contact-to-dealer disclosure a FORMAL (priced) bid triggers; null for a pass. */
    disclosure_event_id: uuid.nullable(),
});
// --- operator: deals (the tier-independent close) --------------------------
/** POST /api/deals — create a deal from a winning bid (operator path; the accept path is automatic). */
export const createDealInput = z.object({
    winning_bid_id: uuid,
    winning_offer_id: uuid.optional(),
    tier_context: z.enum(TIER_CONTEXTS).optional(),
});
/** PATCH /api/deals/:id — advance the status (matched→intro_made→in_progress) or note an outcome reason. */
export const updateDealInput = z
    .object({
    status: z.enum(['matched', 'intro_made', 'in_progress']).optional(),
    outcome_reason: z.string().max(2000).nullable().optional(),
})
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one field is required' });
/**
 * M12 — the operator-recorded close-out attribution shared by the close endpoint + the standalone
 * close-out patch. `fallout_reason` is the STRUCTURED taxonomy for a deal that doesn't close (the
 * free-text `outcome_reason` is kept alongside it). Confirmations capture each side's report of the
 * outcome (Motoveo is out of the money/title loop — a confirmed outcome is the close signal we get).
 * Testimonial fields let a WIN become a marketing asset WITH consent. All optional/nullable.
 */
const closeOutFields = {
    fallout_reason: z.enum(FALLOUT_REASONS).nullable().optional(),
    seller_confirmed: z.boolean().nullable().optional(),
    dealer_confirmed: z.boolean().nullable().optional(),
    testimonial_permission: z.boolean().nullable().optional(),
    testimonial_attribution: z.enum(TESTIMONIAL_ATTRIBUTIONS).nullable().optional(),
    testimonial_quote: z.string().max(4000).nullable().optional(),
};
/**
 * POST /api/deals/:id/close — the explicit close-out step: close-won/lost/fell-through, final price if
 * disclosed, fallout reason if applicable, each side's confirmation, and testimonial consent. A
 * closed_won with a price triggers the (existing) realized-sale comp contribution to the oracle.
 */
export const closeDealInput = z
    .object({
    status: z.enum(['closed_won', 'closed_lost', 'fell_through']),
    outcome: z.enum(DEAL_OUTCOMES).optional(),
    closed_price_cents: cents.positive().optional(),
    outcome_reason: z.string().max(2000).optional(),
    ...closeOutFields,
})
    .refine((o) => o.status !== 'closed_won' || o.closed_price_cents != null, {
    message: 'a closed_won deal requires closed_price_cents (it writes the realized-sale comp)',
    path: ['closed_price_cents'],
});
/**
 * PATCH /api/deals/:id/close-out — record/update close-out attribution + learning on an EXISTING deal
 * (open OR already-closed), WITHOUT re-running the status close. This is how a FAILED deal keeps
 * yielding reusable data: the operator captures the fallout reason, each side's confirmation, and
 * testimonial consent AFTER the fact (feedback arrives later than the close). `null` clears a field.
 */
export const closeOutInput = z
    .object({
    outcome_reason: z.string().max(2000).nullable().optional(),
    ...closeOutFields,
})
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one close-out field is required' });
/**
 * POST /api/deals/:id/feedback — upsert ONE side's NPS (0–10) + comment (party seller|dealer). One row
 * per party per deal (re-posting the same party updates it). Needs an nps or a non-empty comment.
 */
export const dealFeedbackInput = z
    .object({
    party: z.enum(FEEDBACK_PARTIES),
    nps: z.number().int().min(0).max(10).nullable().optional(),
    comment: z.string().max(4000).nullable().optional(),
})
    .refine((o) => o.nps != null || (o.comment != null && o.comment.trim().length > 0), {
    message: 'feedback needs an nps (0–10) or a comment',
    path: ['nps'],
});
/** POST /api/deals/:id/feedback → the upserted feedback row. */
export const dealFeedbackResponseSchema = z.object({ feedback: dealFeedbackSchema });
/** A single reveal in a deal's audited disclosure timeline. */
export const disclosureTimelineItemSchema = disclosureEventSchema.pick({
    id: true,
    reveal_type: true,
    trigger_reason: true,
    triggered_by: true,
    created_at: true,
});
/** A row on the deal-tracker board. */
export const dealBoardItemSchema = z.object({
    id: uuid,
    seller_submission_id: uuid,
    vehicle_id: uuid,
    public_ref: z.string().nullable(),
    dealer_id: uuid,
    dealer_business_name: z.string(),
    model_designation: z.string().nullable(),
    tier_context: z.enum(TIER_CONTEXTS),
    status: z.enum(DEAL_STATUSES),
    outcome: z.string().nullable(),
    agreed_price_cents: cents.nullable(),
    closed_price_cents: cents.nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
    closed_at: isoTimestamp.nullable(),
});
export const dealBoardResponseSchema = z.object({ items: z.array(dealBoardItemSchema) });
/** GET /api/deals/:id — the full deal + winning offer/bid + the disclosure timeline + realized comp. */
export const dealDetailSchema = z.object({
    deal: dealSchema,
    dealer_business_name: z.string(),
    model_designation: z.string().nullable(),
    public_ref: z.string().nullable(),
    winning_offer: offerSchema.nullable(),
    winning_bid: bidSummarySchema.nullable(),
    disclosure_timeline: z.array(disclosureTimelineItemSchema),
    /** the realized_sale comp written on close_won (null until then). */
    realized_comp_id: uuid.nullable(),
    /** M15 — true when that realized comp was audit-voided (a corrected/poisoned oracle row). */
    realized_comp_voided: z.boolean().nullable().optional(),
    /** M12 — seller + dealer NPS/feedback captured for this deal (both sides, when collected). */
    feedback: z.array(dealFeedbackSchema),
});
/** POST /api/deals and PATCH /api/deals/:id → the deal id + its current status. */
export const dealMutationResponseSchema = z.object({ id: uuid, status: z.enum(DEAL_STATUSES) });
// ---------------------------------------------------------------------------
// M12 FUNNEL / LEARNING DASHBOARD — GET /api/cockpit/funnel
// The "are we creating real liquidity or just theater" instrument (work/launch-council.md §5):
// dealer response rate · bids per car · bid spread vs asking · close rate · time-to-close · win/loss ·
// top fallout reasons · top dealer pass reasons · NPS averages · per-dealer reliability. Set-based.
// ---------------------------------------------------------------------------
/** A reason→count tally (fallout reasons + dealer pass reasons), highest first. */
export const funnelReasonTallySchema = z.object({ reason: z.string(), count: z.number().int() });
/** One per-dealer reliability row (reuses the M9 blast_recipients + bids response derivation). */
export const dealerReliabilityRowSchema = z.object({
    dealer_id: uuid,
    dealer_business_name: z.string(),
    asked: z.number().int(),
    reached: z.number().int(),
    responded: z.number().int(),
    bids: z.number().int(),
    no_bids: z.number().int(),
    response_rate: z.number().nullable(),
    bid_rate: z.number().nullable(),
    avg_turnaround_hours: z.number().nullable(),
    last_responded_at: isoTimestamp.nullable(),
});
/** GET /api/cockpit/funnel — fleet-wide funnel + learning metrics across all deals/blasts. */
export const funnelDashboardSchema = z.object({
    generated_at: isoTimestamp,
    totals: z.object({
        submissions: z.number().int(),
        blasted_submissions: z.number().int(),
        blasts: z.number().int(),
        deals: z.number().int(),
        priced_bids: z.number().int(),
    }),
    /** dealer engagement across every blast_recipient we ever asked. */
    demand: z.object({
        asked: z.number().int(),
        reached: z.number().int(), // sent/delivered (the fair denominator)
        responded: z.number().int(),
        bids: z.number().int(),
        no_bids: z.number().int(),
        ignored: z.number().int(),
        response_rate: z.number().nullable(), // responded / reached
        bid_rate: z.number().nullable(), // bids / reached
        bids_per_car: z.number().nullable(), // priced bids / blasted submissions
        cars_with_bids: z.number().int(),
    }),
    /** the spread of priced bids, in cents and as a % of the seller's asking price. */
    bid_spread: z.object({
        count: z.number().int(),
        min_cents: cents.nullable(),
        median_cents: cents.nullable(),
        max_cents: cents.nullable(),
        with_asking_count: z.number().int(),
        min_pct_of_asking: z.number().nullable(),
        median_pct_of_asking: z.number().nullable(),
        max_pct_of_asking: z.number().nullable(),
    }),
    /** the close picture — the direct measure of real liquidity. */
    close: z.object({
        total_deals: z.number().int(),
        open: z.number().int(),
        closed_won: z.number().int(),
        closed_lost: z.number().int(),
        fell_through: z.number().int(),
        resolved: z.number().int(), // won + lost + fell_through
        close_rate: z.number().nullable(), // won / resolved
        avg_time_to_close_hours: z.number().nullable(), // created → closed, over closed_won
        realized_comp_count: z.number().int(), // comps(realized_sale) sourced from deals
        seller_confirmed: z.number().int(),
        dealer_confirmed: z.number().int(),
        testimonial_permission: z.number().int(),
    }),
    fallout_reasons: z.array(funnelReasonTallySchema),
    pass_reasons: z.array(funnelReasonTallySchema),
    nps: z.object({
        seller_avg: z.number().nullable(),
        seller_count: z.number().int(),
        dealer_avg: z.number().nullable(),
        dealer_count: z.number().int(),
    }),
    dealer_reliability: z.array(dealerReliabilityRowSchema),
});
// ---------------------------------------------------------------------------
// M11 FOUNDER VALUATION / APPRAISAL WORKSHEET — work/launch-council.md §4
// "Before launching a car, founders can tell whether the seller's expected number is within plausible
// dealer-bid range." A practical founder tool, NOT an ML oracle: every adjustment is a transparent,
// operator-editable line item; comps are entered by hand/CSV (no scraping); the dealer-acq band feeds
// the M10 launch gate. Comps live in the existing `comps` table keyed by spec_fingerprint.
// ---------------------------------------------------------------------------
/**
 * Add ONE external comp by hand (POST /api/comps). Model/generation are required so the comp keys to a
 * spec_fingerprint (and matches loosely by model+generation). `price_kind` distinguishes a SOLD price
 * (realized) from a LISTING (asking → gets the transparent listing→sold discount). `is_sample` marks it
 * as illustrative seed data (source_ref='seed:sample') for easy purge — founders enter real comps.
 */
export const addCompInput = z.object({
    comp_type: z.enum(EXTERNAL_COMP_TYPES),
    price_cents: cents.positive(),
    price_kind: z.enum(COMP_PRICE_KINDS).optional(),
    source: z.enum(EXTERNAL_COMP_SOURCES).default('operator_manual'),
    source_url: z.string().url().max(2000).optional(),
    observed_at: isoTimestamp.optional(),
    mileage: z.number().int().nonnegative().max(1_000_000).optional(),
    condition_grade: z.enum(CONDITION_GRADES).optional(),
    title_status: z.enum(TITLE_STATUSES).optional(),
    region_label: z.string().max(120).optional(),
    notes: z.string().max(2000).optional(),
    // spec keying (mirrors the fingerprint facets) --------------------------------------------------
    model_designation: z.string().min(1).max(80),
    generation: z.string().min(1).max(20),
    model_year: z.number().int().min(1950).max(2100).optional(),
    transmission: z.enum(TRANSMISSIONS).optional(),
    paint_to_sample: z.boolean().optional(),
    exterior_color: z.string().max(120).optional(),
    pts_color_name: z.string().max(120).optional(),
    interior: z.string().max(120).optional(),
    high_value_option_codes: z.array(z.string().min(1).max(16)).max(50).optional(),
    is_sample: z.boolean().optional(),
});
/**
 * M15 — POST /api/comps/:id/void: audit-void a POISONED oracle row (e.g. a fat-fingered closed_won
 * price). The row is NEVER deleted (append-only moat); a reason is REQUIRED (it is an audited
 * correction) and every oracle/gate/valuation reader ignores voided rows (M14/M2).
 */
export const voidCompInput = z.object({
    reason: z.string().trim().min(3).max(2000),
});
/** POST /api/comps/:id/void → the voided comp id + when. */
export const voidCompResponseSchema = z.object({
    id: uuid,
    voided_at: isoTimestamp,
});
/** One row of a comps CSV import (same fields as a hand-entered comp). */
export const compImportRowInput = addCompInput;
/** POST /api/comps/import — bulk comp import (dry-run by default; idempotent on source_url). */
export const compImportInput = z.object({
    rows: z.array(compImportRowInput).min(1).max(2000),
    /** true (default) = validate + report what WOULD happen; false = actually write. */
    dry_run: z.boolean().default(true),
    /** mark every imported comp as illustrative sample/seed data (source_ref='seed:sample'). */
    as_sample: z.boolean().default(false),
});
/** One import row outcome (created | updated | skipped | error) — mirrors the dealer-import report. */
export const compImportRowResultSchema = z.object({
    index: z.number().int(),
    label: z.string(),
    outcome: z.enum(['created', 'updated', 'skipped', 'error']),
    comp_id: uuid.nullable(),
    detail: z.string(),
});
/** POST /api/comps/import → the dry-run/live import report. */
export const compImportResultSchema = z.object({
    dry_run: z.boolean(),
    total: z.number().int(),
    created: z.number().int(),
    updated: z.number().int(),
    skipped: z.number().int(),
    errors: z.number().int(),
    rows: z.array(compImportRowResultSchema),
});
/** A comp row as shown on the worksheet (the subset of the `comps` table the appraisal reads). */
export const compRowSchema = z.object({
    id: uuid,
    comp_type: z.enum(COMP_TYPES),
    price_cents: cents.nullable(),
    price_kind: z.string().nullable(),
    mileage: z.number().int().nullable(),
    condition_grade: z.string().nullable(),
    title_status: z.string().nullable(),
    region_label: z.string().nullable(),
    source: z.enum(COMP_SOURCES),
    source_url: z.string().nullable(),
    notes: z.string().nullable(),
    model_designation: z.string().nullable(),
    generation: z.string().nullable(),
    model_year: z.number().int().nullable(),
    high_value_option_codes: z.array(z.string()),
    is_sample: z.boolean(),
    /** true for a comp a founder may edit/delete (external/manual) vs a protected moat comp (bid/deal). */
    editable: z.boolean(),
    observed_at: isoTimestamp,
    created_at: isoTimestamp,
});
/**
 * The transparent, operator-EDITABLE valuation assumptions. Every field has a documented default (see
 * @motoveo/core DEFAULT_VALUATION_ASSUMPTIONS); an operator override tunes ONE run. No black box.
 */
export const valuationAssumptionsSchema = z.object({
    /** mileage adjustment, $/1,000-mi delta (a lower-mileage subject is worth MORE than a higher-mileage comp). */
    mileage_adj_per_1k_cents: cents.nonnegative(),
    /** dealer gross-margin target applied to retail to estimate acquisition (0..1). */
    dealer_margin_pct: z.number().min(0).max(0.9),
    /** flat recon + transport + reconditioning buffer subtracted from the dealer-acq band. */
    recon_cents: cents.nonnegative(),
    /** a LISTING (asking) comp is discounted this much to approximate a sold price (0..1). */
    listing_to_sold_discount_pct: z.number().min(0).max(0.9),
    /** condition adjustment, $ per grade step between the subject and a comp. */
    condition_step_cents: cents.nonnegative(),
    /** flat per-option-code value deltas (code → cents; applied when subject/comp option sets differ). */
    option_deltas_cents: z.record(z.number().int()),
    /** headroom above the band high before an anchor flips closable → marginal → unrealistic (0..1). */
    tolerance_pct: z.number().min(0).max(1),
    /** loose = match comps by model+generation; exact = match by full spec_fingerprint. */
    match_mode: z.enum(['loose', 'exact']),
    /** when set, only comps within subject model_year ± this window are used (null = no year filter). */
    year_window: z.number().int().min(0).max(30).nullable(),
});
/** Partial assumption override (PUT/POST valuation) — any subset tunes that run over the defaults. */
export const valuationAssumptionsInput = valuationAssumptionsSchema.partial();
/** A low/mid/high money band (retail or dealer-acquisition). Fields null when no comps support it. */
export const valuationBandSchema = z.object({
    low_cents: cents.nullable(),
    mid_cents: cents.nullable(),
    high_cents: cents.nullable(),
});
/** A comp that contributed to the range, with its FULLY TRANSPARENT per-line adjustment breakdown. */
export const valuationCompSchema = compRowSchema.extend({
    /** the comp's price adjusted to the SUBJECT car (raw price + the deltas below). */
    adjusted_value_cents: cents,
    adjustments: z.object({
        mileage_cents: z.number().int(),
        options_cents: z.number().int(),
        condition_cents: z.number().int(),
        listing_discount_cents: z.number().int(),
    }),
});
/**
 * The computed appraisal worksheet (GET/PUT /api/submissions/:id/valuation). Retail range → estimated
 * dealer-acquisition band → seller-floor gap → verdict, plus the comps that drove it, the assumptions
 * used, and whether a saved valuation is wired into the launch gate. Everything is derived live.
 */
export const valuationSchema = z.object({
    submission_id: uuid,
    vehicle_id: uuid.nullable(),
    spec_fingerprint: z.string().nullable(),
    model_designation: z.string().nullable(),
    generation: z.string().nullable(),
    model_year: z.number().int().nullable(),
    subject_mileage: z.number().int().nullable(),
    subject_condition_grade: z.string().nullable(),
    subject_high_value_option_codes: z.array(z.string()),
    // the appraisal ------------------------------------------------------------------------------
    retail: valuationBandSchema,
    dealer_acq: valuationBandSchema,
    dealer_acq_source: z.enum(DEALER_ACQ_SOURCES).nullable(),
    seller_anchor_cents: cents.nullable(),
    anchor_source: z.enum(VALUATION_ANCHOR_SOURCES).nullable(),
    /** anchor − dealer_acq.high (positive = seller is above the band). null when either is missing. */
    gap_cents: cents.nullable(),
    gap_pct: z.number().nullable(),
    verdict: z.enum(VALUATION_VERDICTS),
    confidence: z.enum(CONFIDENCE_LEVELS),
    comp_count: z.number().int(),
    retail_comp_count: z.number().int(),
    dealer_comp_count: z.number().int(),
    reasons: z.array(z.string()),
    assumptions: valuationAssumptionsSchema,
    comps: z.array(valuationCompSchema),
    /** the persisted valuation state (null until the operator saves one) + its launch-gate wiring. */
    persisted: z
        .object({
        use_for_gate: z.boolean(),
        updated_at: isoTimestamp,
    })
        .nullable(),
});
/** PUT /api/submissions/:id/valuation — recompute with overrides, then persist (+ optionally wire the gate). */
export const saveValuationInput = z.object({
    assumptions: valuationAssumptionsInput.optional(),
    /** persist this valuation as the band the M10 launch gate consumes (source 'valuation'). */
    use_for_gate: z.boolean().optional(),
});
/** GET /api/submissions/:id/valuation → the live worksheet + its comps table. */
export const valuationResponseSchema = z.object({ valuation: valuationSchema });
/** POST /api/comps → the created comp. */
export const addCompResponseSchema = z.object({ comp: compRowSchema });
//# sourceMappingURL=api.js.map